import { Maybe } from 'maybeasy';
import { err, ok, Result } from 'resulty';

export interface MissingEmailError {
  kind: 'missing-email-error';
}
export interface InvalidEmailError {
  kind: 'invalid-email-error';
}

const invalidEmailError = (): InvalidEmailError => ({
  kind: 'invalid-email-error',
});

const missingEmailError = (): MissingEmailError => ({
  kind: 'missing-email-error',
});

export type EmailError = MissingEmailError | InvalidEmailError;

type EmailAddress = string;

interface EAddress {
  recipient: string;
  domain: string;
}

const recipient = (candidate: string): Result<InvalidEmailError, [string, string]> => {
  const parts = candidate.split('@');
  return parts.length !== 2 ? err(invalidEmailError()) : ok([parts[0], parts[1]]);
};

const domain = (candidate: string): Result<InvalidEmailError, string> => {
  return candidate.split('.').length < 2 ? err(invalidEmailError()) : ok(candidate);
};

const eAddress = (candidate: string): Result<EmailError, EAddress> => {
  return recipient(candidate)
    .map<[{ recipient: string }, string]>(([recipient, domain]) => [{ recipient }, domain])
    .andThen(([partial, candidate]) => domain(candidate).map((d) => ({ ...partial, domain: d })));
};

const toEmailString = ({ recipient, domain }: EAddress): string => {
  return `${recipient}@${domain}`;
};

export const validateMaybeEmail = (email: Maybe<string>): Result<EmailError, EmailAddress> => {
  return email
    .map<Result<EmailError, EmailAddress>>(ok)
    .getOrElse(() => err(missingEmailError()))
    .andThen(eAddress)
    .map(toEmailString);
};

export const validateEmail = (email: string): Result<EmailError, EmailAddress> =>
  ok<EmailError, string>(email).andThen(eAddress).map(toEmailString);
